import type { FC } from 'react';

import { Accordion } from '@coop/components';
import classnames from 'classnames';
import * as React from 'react';

import type {
    ChildrenRenderBehavior,
    SideMenuExpandClickHandler,
    SideMenuLinkClickHandler,
} from './SideMenu.types';

import { type MenuKey, type MenuNode } from '../../../hooks/useMenuState';
import { isAriaCurrentPage } from '../../../utils/accessibilityUtils';
import styles from './SideMenu.module.scss';

interface MenuItemProps {
    menuNode: MenuNode;
    currentPath: string;
    activeNodesTrail: MenuKey[];
    expandedNodesTrail: MenuKey[];
    onExpandClick: SideMenuExpandClickHandler;
    onLinkClick: SideMenuLinkClickHandler;
    parentId?: MenuKey;
    level: number;
    childrenRenderBehavior: ChildrenRenderBehavior;
}

// Wrapper component to remove some of accordion controls when Accordion item is a link
const Link = React.forwardRef<HTMLAnchorElement, React.ComponentPropsWithoutRef<'a'>>(
    (props, ref) => {
        const {
            'aria-expanded': _ariaExpanded,
            'aria-controls': _ariaControls,
            type: _type,
            ...rest
        } = props;

        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <a {...rest} ref={ref}>
                {props.children}
            </a>
        );
    },
);

const SideMenuItem: FC<React.PropsWithChildren<MenuNode & MenuItemProps>> = ({
    menuNode,
    currentPath,
    id,
    url,
    name,
    onExpandClick,
    onLinkClick,
    nodes,
    activeNodesTrail,
    expandedNodesTrail,
    parentId,
    level,
    type,
    // oxlint-disable-next-line oxc/only-used-in-recursion -- pass-through to nested SideMenuItem
    childrenRenderBehavior,
}) => {
    const hasChildren = !!nodes?.length;
    const repeatedLinkLabel = menuNode.displayName ?? name;
    const expandedNodeId = nodes.find((node) => expandedNodesTrail.includes(node.id))?.id as
        | string
        | undefined;

    const active = activeNodesTrail[activeNodesTrail.length - 1] === id;
    const ariaCurrentPage = active && isAriaCurrentPage(url || '', currentPath);

    return (
        <Accordion.Item value={id as string} asChild>
            <li className={classnames(styles.Item, level > 1 && styles.SubItem)}>
                <Accordion.Header asChild>
                    {hasChildren ? (
                        <Accordion.Trigger
                            onClick={(e) =>
                                onExpandClick({
                                    e,
                                    id,
                                    parentId,
                                    level,
                                    hasChildren,
                                    type,
                                    name,
                                })
                            }
                            className={classnames(styles.Trigger)}
                            iconClassName={styles.Chevron}
                        >
                            {menuNode.renderBody ? (
                                menuNode.renderBody(menuNode, expandedNodesTrail.includes(id))
                            ) : (
                                <span>{name}</span>
                            )}
                        </Accordion.Trigger>
                    ) : (
                        <Accordion.Trigger asChild withChevron={false}>
                            <Link
                                href={url}
                                className={classnames(
                                    styles.Trigger,
                                    styles.Link,
                                    active && styles.Active,
                                )}
                                onClick={(e) => {
                                    onLinkClick({
                                        e,
                                        url: url!,
                                        id,
                                        parentId,
                                        hasChildren,
                                        type,
                                        name,
                                    });
                                }}
                                aria-current={ariaCurrentPage}
                            >
                                {menuNode.renderBody ? (
                                    menuNode.renderBody(menuNode, expandedNodesTrail.includes(id))
                                ) : (
                                    <span>{name}</span>
                                )}
                            </Link>
                        </Accordion.Trigger>
                    )}
                </Accordion.Header>

                {hasChildren && (
                    <Accordion.Content
                        className={classnames(styles.Content, level >= 2 && styles.Sub)}
                    >
                        <Accordion.Root collapsible type="single" asChild value={expandedNodeId}>
                            <ul>
                                {/* repeated node to be able to go to it as link */}
                                <Accordion.Item value={`${id}_repeated`} asChild>
                                    <li
                                        className={classnames(
                                            styles.Item,
                                            level + 1 > 1 && styles.SubItem,
                                        )}
                                    >
                                        <Accordion.Header asChild>
                                            <Accordion.Trigger asChild withChevron={false}>
                                                <Link
                                                    href={url}
                                                    className={classnames(
                                                        styles.Trigger,
                                                        styles.Link,
                                                        active && styles.Active,
                                                    )}
                                                    onClick={(e) => {
                                                        onLinkClick({
                                                            e,
                                                            url: url!,
                                                            id,
                                                            parentId,
                                                            hasChildren,
                                                            type,
                                                            name,
                                                        });
                                                    }}
                                                    aria-current={ariaCurrentPage}
                                                >
                                                    <span>{repeatedLinkLabel}</span>
                                                </Link>
                                            </Accordion.Trigger>
                                        </Accordion.Header>
                                    </li>
                                </Accordion.Item>
                                {nodes?.map((item) => (
                                    <SideMenuItem
                                        menuNode={item}
                                        currentPath={currentPath}
                                        key={item.id}
                                        id={item.id}
                                        name={item.name}
                                        url={item.url}
                                        segmentUrl={item.segmentUrl}
                                        onExpandClick={onExpandClick}
                                        onLinkClick={onLinkClick}
                                        ancestors={item.ancestors}
                                        nodes={item.nodes}
                                        activeNodesTrail={activeNodesTrail}
                                        expandedNodesTrail={expandedNodesTrail}
                                        parentId={id}
                                        level={level + 1}
                                        type={item.type}
                                        childrenRenderBehavior={childrenRenderBehavior}
                                    />
                                ))}
                            </ul>
                        </Accordion.Root>
                    </Accordion.Content>
                )}
            </li>
        </Accordion.Item>
    );
};

export default SideMenuItem;
